import { LocationPoint, PlacesSearchType } from 'features/Directory'
import { includes } from 'lodash-es'
import { types } from 'mobx-state-tree'
import moment from 'moment'
import { isEuropeCountryCode } from 'service/ISO'
import { DateFormatMoment } from 'utils/date'

import { CONTAINER_TYPE_22GP, CONTAINER_TYPE_42GP } from '../Cargo'
import {
  CARRIAGE_MODE_AIR,
  CARRIAGE_MODE_RAIL,
  CARRIAGE_MODE_SEA,
  CARRIAGE_MODE_SEA_AIR,
  CARRIAGE_TYPE_FCL,
  CARRIAGE_TYPE_LCL,
  TCarriageMode,
  TCarriageType,
} from '../Carriage'
import { BookingParty } from '../Incoterms/incotermsMeta'
import { insuranceTypes } from '../QuoteRequestInsurance'
import { CarbonOffsetStrategy } from '../QuoteServices'
import {
  BuyersConsolidationQuoteServicesMst,
  TBuyersConsolidationQuoteServicesMst,
} from './BuyersConsolidationServicesMst'
import { FclCargoMst, IFclCargoMst, ILclCargoMst, LclCargoMst } from './Cargo'
import { CarriageModeEnum, CarriageTypeEnum } from './Carriage'
import { QuoteRequestInsuranceMst } from './QuoteRequestInsuranceMst'

const CurrencyMst = types.model('Currency', {
  value: types.number,
  code: types.string,
})

const Form = types
  .model('Form', {
    triedSubmit: types.optional(types.boolean, false),
    serverError: '',
  })
  .actions((self) => ({
    setServerError(serverError: string) {
      self.serverError = serverError
    },
  }))
  .actions((self) => ({
    trySubmit() {
      self.triedSubmit = true
      self.setServerError('')
    },
  }))

export const getOriginPortType = (mode: TCarriageMode) => (mode === 'seaAir' ? 'sea' : mode)
export const getDestinationPortType = (mode: TCarriageMode) => (mode === 'seaAir' ? 'air' : mode)

const ensureCargoTypesExist = (cargo: (IFclCargoMst | ILclCargoMst)[] = []) => {
  if (!cargo.some((c) => c.type === 'fcl')) {
    cargo.push(FclCargoMst.create())
  }
  if (!cargo.some((c) => c.type === 'lcl')) {
    cargo.push(LclCargoMst.create())
  }
  return cargo
}

const CarbonOffsetService = types.model('CarbonOffsetService', {
  offsetStrategy: types.enumeration('offsetStrategy', [CarbonOffsetStrategy.All, CarbonOffsetStrategy.None]),
})

const QuoteRequestMstBase = types
  .model('QuoteRequest', {
    form: types.optional(Form, () => Form.create()),
    type: types.optional(CarriageTypeEnum, CARRIAGE_TYPE_FCL),
    modes: types.optional(types.array(CarriageModeEnum), () => [CARRIAGE_MODE_SEA]),
    origin: types.maybe(LocationPoint),
    destination: types.maybe(LocationPoint),
    insuranceEnabled: types.optional(types.boolean, false),
    insurance: types.optional(types.array(QuoteRequestInsuranceMst), () =>
      insuranceTypes.map((ins) => QuoteRequestInsuranceMst.create(ins))
    ),
    hsCodesCount: types.optional(types.number, 1),
    specialEquipment: types.optional(types.boolean, false),
    dangerousGoods: types.optional(types.boolean, false),
    dangerousGoodsAdditionalDetails: types.maybe(types.string),
    vgm: types.optional(types.boolean, false),
    goodsValue: types.maybe(CurrencyMst),
    buyersConsolidation: types.maybe(BuyersConsolidationQuoteServicesMst),
    bookingParty: types.optional(
      types.enumeration('bookingParty', [BookingParty.Buyer, BookingParty.Seller]),
      BookingParty.Buyer
    ),
    isBookingPartyAgent: types.optional(types.boolean, false),
    incoTerm: types.maybe(types.string),
    carbonOffsetService: types.maybe(CarbonOffsetService),
  })
  .views((self) => ({
    isMode(mode: TCarriageMode) {
      return self.modes.length === 1 && self.modes[0] === mode
    },
    hasMode(mode: TCarriageMode) {
      return self.modes.indexOf(mode) > -1
    },
    get isInsuranceClassChecked() {
      return self.insurance.find((insurance) => insurance.checked) != null
    },
    get originCountryCode() {
      return self.origin && (self.origin.countryCode || self.origin.country)
    },
    get destinationCountryCode() {
      return self.destination && (self.destination.countryCode || self.destination.country)
    },
    get hasBuyersConsolidation(): boolean {
      return Boolean(self.buyersConsolidation && self.buyersConsolidation.enabled)
    },
    get isBookingPartyBuyer(): boolean {
      return self.bookingParty === BookingParty.Buyer
    },
    get isBookingPartySeller(): boolean {
      return self.bookingParty === BookingParty.Seller
    },
  }))
  .views((self) => ({
    get isOriginValid() {
      return !!self.origin
    },
    get isDestinationValid() {
      return !!self.destination
    },
    get isLcl() {
      return self.type === CARRIAGE_TYPE_LCL
    },
    get isFcl() {
      return self.type === CARRIAGE_TYPE_FCL
    },
    get isRail() {
      return self.isMode(CARRIAGE_MODE_RAIL)
    },
    get isAir() {
      return self.isMode(CARRIAGE_MODE_AIR)
    },
    get hasAir() {
      return self.hasMode(CARRIAGE_MODE_AIR)
    },
    get hasSeaAir() {
      return self.hasMode(CARRIAGE_MODE_SEA_AIR)
    },
    get isSea() {
      return self.isMode(CARRIAGE_MODE_SEA)
    },
    get isSeaAir() {
      return self.isMode(CARRIAGE_MODE_SEA_AIR)
    },
    get isDestinationUSA() {
      return self.destinationCountryCode === 'US'
    },
    get isOriginEUCountry() {
      return isEuropeCountryCode(self.originCountryCode as string)
    },
  }))
  .views((self) => ({
    get showOriginError() {
      return self.form.triedSubmit && !self.isOriginValid
    },
    get showDestinationError() {
      return self.form.triedSubmit && !self.isDestinationValid
    },
    get destinationString() {
      return self.destination && self.destination.string
    },
    get isAirDestination() {
      return self.isAir || self.isSeaAir
    },
    get originTypes(): PlacesSearchType[] {
      return ['address', ...self.modes.map((mode) => getOriginPortType(mode as TCarriageMode))]
    },
    get destinationTypes(): PlacesSearchType[] {
      return ['address', ...self.modes.map((mode) => getDestinationPortType(mode as TCarriageMode))]
    },
    get isValid() {
      return self.isOriginValid && self.isDestinationValid
    },
    get modeSupportsBuyersConsolidation(): boolean {
      // Buyers consolidation is only supported for sea.
      return self.isSea && !self.isSeaAir
    },
    get typeSupportsBuyersConsolidation(): boolean {
      // Buyers consolidation is only supported for FCL.
      return self.isFcl
    },
    /**
     * Buyers consolidation is only china, hong kong, vietnam and bangladesh.
     */
    get originSupportsBuyersConsolidation(): boolean {
      const buyersConsolidationCountryCodes = ['CN', 'VN', 'BD', 'HK']
      return self.originCountryCode ? buyersConsolidationCountryCodes.indexOf(self.originCountryCode) >= 0 : false
    },

    get isOffsetAll(): boolean {
      return self.carbonOffsetService?.offsetStrategy === CarbonOffsetStrategy.All
    },
  }))
  .actions((self) => {
    function setModes(modes: TCarriageMode[]): void {
      self.modes = modes
      // Only supported by a subset of modes
      // This is not reactive, alternatives considered: doing it in the component, using autorun.
      if (!self.modeSupportsBuyersConsolidation) {
        // "as unknown as TBuyersConsolidationQuoteServicesMst" to keep betterer happy without the need of refactor
        // This will be fixed with the non mobx version
        self.buyersConsolidation = undefined as unknown as TBuyersConsolidationQuoteServicesMst
      }
    }
    return {
      setModes,
      postProcessSnapshot(snapshot) {
        const override = { ...snapshot }
        delete override.form
        return override
      },
      setMode(mode: TCarriageMode) {
        setModes([mode])
      },
      setGoodsValue(value: number) {
        self.goodsValue = { value: Number(value), code: 'EUR' }
      },
      setBuyersConsolidation(buyersConsolidation?: TBuyersConsolidationQuoteServicesMst) {
        self.buyersConsolidation = buyersConsolidation as TBuyersConsolidationQuoteServicesMst
      },
      ensureSingleModeSelected() {
        if (self.modes.length === 1) {
          return
        }
        setModes([self.modes[0] as TCarriageMode])
      },
      setBookingParty(bookingParty: BookingParty) {
        self.bookingParty = bookingParty
      },
      toggleIsBookingPartyAgent() {
        self.isBookingPartyAgent = !self.isBookingPartyAgent
      },
      setIncoTerm(incoTerm: string | null) {
        self.incoTerm = incoTerm
      },
      setCarbonOffset(enabled: boolean) {
        self.carbonOffsetService = { offsetStrategy: enabled ? CarbonOffsetStrategy.All : CarbonOffsetStrategy.None }
      },
    }
  })
  .actions((self) => ({
    setType(type: TCarriageType) {
      self.type = type
      if (type === 'fcl') {
        self.ensureSingleModeSelected()
      }
      if (type === 'lcl') {
        self.setBuyersConsolidation()
      }
    },
  }))
  .actions((self) => ({
    setOrigin(origin) {
      self.origin = origin
      // This is not reactive, alternatives considered: doing it in the component, using autorun.
      if (!self.originSupportsBuyersConsolidation) {
        // "as unknown as TBuyersConsolidationQuoteServicesMst" to keep betterer happy without the need of refactor
        // This will be fixed with the non mobx version
        self.buyersConsolidation = undefined as unknown as TBuyersConsolidationQuoteServicesMst
      }
      if (self.isOriginEUCountry) {
        self.setBookingParty(BookingParty.Seller)
      } else {
        self.setBookingParty(BookingParty.Buyer)
      }
    },
    setDestination(destination) {
      self.destination = destination
    },
    toggleMode(mode: TCarriageMode) {
      const shouldAddMode = !self.hasMode(mode)
      // At least 1 mode has to be set
      const canRemoveMode = self.modes.length > 1
      if (shouldAddMode) {
        const modes = self.isLcl ? [...self.modes, mode] : [mode]
        self.setModes(modes as TCarriageMode[])

        if (self.hasAir || self.hasSeaAir) {
          self.setType('lcl')
        }
      } else if (canRemoveMode) {
        self.setModes(self.modes.filter((currentMode) => currentMode !== mode) as TCarriageMode[])
      }
    },
    setSpecialEquipment(specialEquipment: boolean) {
      self.specialEquipment = specialEquipment
    },
    setDangerousGoods(dangerousGoods: boolean) {
      self.dangerousGoods = dangerousGoods
    },
    setDangerousGoodsAdditionalDetails(dangerousGoodsAdditionalDetails: string) {
      self.dangerousGoodsAdditionalDetails = dangerousGoodsAdditionalDetails
    },
    setVgm(vgm: boolean) {
      self.vgm = vgm
    },
    setInsuranceEnabled(insuranceEnabled: boolean) {
      self.insuranceEnabled = insuranceEnabled
    },
    setHsCodesCount(hsCodesCount: string | number) {
      self.hsCodesCount = Number(hsCodesCount)
    },
  }))

const QuoteRequestWithCustoms = types
  .compose(
    QuoteRequestMstBase,
    types.model('QuoteRequestWithCustoms', {
      customs: types.optional(types.boolean, false),
      customsIssuance: types.optional(types.boolean, false),
      customsBondType: '',
    })
  )
  .actions((self) => ({
    setCustoms(customs: boolean) {
      self.customs = customs
    },
    setCustomsBondType(bondType: string) {
      self.customsBondType = bondType
    },
    setCustomsIssuance(customsIssuance: boolean) {
      self.customsIssuance = customsIssuance
    },
  }))

export const QuoteRequestMst = types
  .compose(
    QuoteRequestWithCustoms,
    types.model({
      cargoReadyAt: types.maybe(types.Date),
      cargoStackable: types.optional(types.boolean, true),
      cargo: types.optional(
        types.array(
          types.union(
            (cargo) => (cargo.type === CARRIAGE_TYPE_FCL ? FclCargoMst : LclCargoMst),
            FclCargoMst,
            LclCargoMst
          )
        ),
        () => [FclCargoMst.create(), LclCargoMst.create()]
      ),
    })
  )
  .preProcessSnapshot((snapshot) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    const { cargoReadyAt } = snapshot || ({} as any)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
    const override = {} as any
    if (cargoReadyAt) {
      override.cargoReadyAt = new Date(cargoReadyAt)
    }
    override.cargo = ensureCargoTypesExist(snapshot.cargo)
    return { ...snapshot, ...override }
  })
  .views((self) => ({
    get cargoLCL(): ILclCargoMst[] {
      return self.cargo.filter((c) => c.type === 'lcl') as ILclCargoMst[]
    },
    get cargoFCL(): IFclCargoMst[] {
      return self.cargo.filter((c) => c.type === 'fcl') as IFclCargoMst[]
    },
  }))
  .views((self) => ({
    get totalVolume() {
      return self.cargoLCL.reduce((accum, item) => {
        return accum + Number(item.volume) * Number(item.quantity)
      }, 0)
    },
    get totalWeight() {
      return self.cargoLCL.reduce((accum, item) => {
        return accum + Number(item.grossWeight) * Number(item.quantity)
      }, 0)
    },
    get isCargoReadyDateValid() {
      if (self.cargoReadyAt) {
        return moment(self.cargoReadyAt).diff(moment(), 'days') >= 0
      }
      return true
    },
    /**
     * Some critical countries which are restricted for booking, MQ
     */
    isRestrictedCountry(allowSyria: boolean = false) {
      // Russia, Ukraine, Belrus, Cuba, Iran, Iraq, North Korea
      const restrictedCountries = ['RU', 'UA', 'BY', 'CU', 'IR', 'IQ', 'KP']

      // Syria should also be inlcuded in the list of unrestricted countries unless specifically overridden using a feature flag
      if (!allowSyria) {
        restrictedCountries.push('SY')
      }
      return (
        includes(restrictedCountries, self.originCountryCode) ||
        includes(restrictedCountries, self.destinationCountryCode)
      )
    },
  }))
  .actions((self) => ({
    postProcessSnapshot(snapshot) {
      const override = { ...snapshot }
      if (snapshot.cargoReadyAt) {
        // Using string instead of ISO string because this will prevent us from having problems
        // with timezones - if it's 2018-02-02 00:00 Berlin time - it will do 2018-02-01 22:00 Zulu time
        // And it screws up the CRD date in transport plan
        override.cargoReadyAt = moment(snapshot.cargoReadyAt).format(DateFormatMoment.DATE).toString()
      }
      override.cargo = snapshot.cargo.filter((c) => c.type === self.type)
      return override
    },
    deleteCargo(cargoItem: IFclCargoMst | ILclCargoMst) {
      self.cargo.remove(cargoItem)
    },
    setCargoReadyAt(cargoReadyAt: Date | moment.Moment) {
      self.cargoReadyAt = moment.isMoment(cargoReadyAt) ? cargoReadyAt.toDate() : cargoReadyAt
    },
    addCargo() {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      const cargo: any = { type: self.type }
      if (self.isFcl) {
        cargo.containerType = self.isRail ? CONTAINER_TYPE_42GP : CONTAINER_TYPE_22GP
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- // TODO: Replace any with a suitable type
      self.cargo.push(cargo as any)
    },
    setCargoStackable(cargoStackable: boolean) {
      self.cargoStackable = cargoStackable
    },
  }))
